import { useRouter } from "next/router";
import { type PropsWithChildren, useEffect } from "react";

import { useOverlayQueueStore } from "@/providers/overlay/overlay-queue.store";
import { logger } from "@/util/logger";

/**
 * Provider that manages overlay queue lifecycle
 * - Clears queue on route changes
 * - Can be extended for other global overlay management logic
 */
export const OverlayQueueProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { clearOverlayQueue, dismissCurrentOverlay, currentOverlay } = useOverlayQueueStore();

  useEffect(() => {
    const handleRouteChangeStart = () => {
      // Clear queue but keep current overlay visible until route changes
      if (currentOverlay) {
        logger.info("[OverlayQueueProvider] Route changing, clearing queue");
        clearOverlayQueue();
      }
    };

    const handleRouteChangeComplete = () => {
      // Dismiss current overlay after route change completes
      if (currentOverlay) {
        logger.info("[OverlayQueueProvider] Route changed, dismissing current overlay");
        dismissCurrentOverlay();
      }
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router.events, clearOverlayQueue, dismissCurrentOverlay, currentOverlay]);

  return <>{children}</>;
};
